import React from 'react';
import { AgCharts } from 'ag-charts-react';
import { IAerEstimator, IVesselCiiAlignments, IVesselLocationChart } from '../../../../greenTypes';
import { AgChartOptions } from 'ag-charts-community';
import { formatValueAsString } from '../../../../../../utils/helpers';
import { ChartContainer } from '../../../../../../shared/components/charts/ChartContainer';

interface IPpClimateAlignmentChartProps {
	chartData: IVesselLocationChart | undefined;
	data: IVesselCiiAlignments[] | undefined;
	historicalPerformanceData: IAerEstimator[] | null;
}

const VesselPpClimateAlignmentChart: React.FC<IPpClimateAlignmentChartProps> = ({ ...props }) => {
	const formatOptions = { maximumFractionDigits: 1 };

	const maxYear = new Date().getFullYear();

	const combinedData = props.chartData?.chartDataItemList.map((item) => {
		const matchingAer = props.historicalPerformanceData?.reduce((prev: any, current) => {
			if (
				parseInt(String(current.year).substring(0, 4)) <= maxYear &&
				parseInt(String(current.year).substring(0, 4)) === item.year
			) {
				return current;
			}
			return prev;
		}, null);

		const maxYearData = props.historicalPerformanceData?.find((data) => parseInt(String(data.year).substring(0, 4)) === maxYear);

		return {
			year: item.year,
			msiEstimate: matchingAer?.aerWtw,
			currentEstimate: (item.year >= maxYear && item.estimated && maxYearData?.aerWtw) || null,
			ppTrajectoryMinimum: item?.ppTrajectoryMinimum,
			ppTrajectoryStriving: item?.ppTrajectoryStriving,
		};
	});

	const options: AgChartOptions = {
		data: combinedData,
		series: [
			{
				type: 'line',
				xKey: 'year',
				yKey: 'msiEstimate',
				yName: props.chartData?.expressions.estimatedRecentValues,
				stroke: '#FFD700',
				marker: {
					shape: 'circle',
					size: 6,
					fill: '#FFD700',
				},
				strokeWidth: 2,
				tooltip: {
					renderer: (params: any) => {
						return {
							data: undefined,
							title: `MSI Estimate (${params.datum.year}): ${formatValueAsString(params.datum.msiEstimate, formatOptions, '-', '', '')}`,
						};
					},
				},
				connectMissingData: true,
			},
			{
				type: 'line',
				xKey: 'year',
				yKey: 'currentEstimate',
				yName: 'Current Estimate (Projected)',
				stroke: '#FFD700',
				strokeWidth: 2,
				lineDash: [4, 4],
				marker: {
					enabled: false,
				},
				tooltip: {
					renderer: () => {
						return {
							data: undefined,
							title: `Current Estimate (Projected): ${formatValueAsString(
								combinedData?.find((item) => item.currentEstimate)?.currentEstimate,
								formatOptions,
								'-',
								'',
								'',
							)}`,
						};
					},
				},
				connectMissingData: true,
			},
			{
				type: 'line',
				xKey: 'year',
				yKey: 'ppTrajectoryMinimum',
				yName: props.chartData?.expressions.ppTrajectoryMinimum,
				stroke: '#00274D',
				strokeWidth: 3,
				marker: {
					enabled: false,
				},
				tooltip: {
					renderer: (params: any) => {
						return {
							data: undefined,
							title: `PP Trajectory (${params.datum.year}): Minimum: ${formatValueAsString(params.datum.ppTrajectoryMinimum, formatOptions, '-', '', '')}`,
						};
					},
				},
				connectMissingData: true,
			},
			{
				type: 'line',
				xKey: 'year',
				yKey: 'ppTrajectoryStriving',
				yName: props.chartData?.expressions.ppTrajectoryStriving,
				stroke: '#87CEFA',
				strokeWidth: 2,
				marker: {
					enabled: false,
				},
				tooltip: {
					renderer: (params: any) => {
						return {
							data: undefined,
							title: `PP Trajectory (${params.datum.year}): Striving: ${formatValueAsString(params.datum.ppTrajectoryStriving, formatOptions, '-', '', '')}`,
						};
					},
				},
				connectMissingData: true,
			},
		],
		axes: [
			{
				type: 'number',
				position: 'left',
				title: {
					text: 'gCO₂/dwt.nm',
				},
			},
			{
				type: 'category',
				position: 'bottom',
				title: {
					text: 'Year',
				},
				label: {
					rotation: 90,
				},
			},
		],
		legend: {
			position: 'bottom',
			item: {
				label: {
					fontSize: 12,
				},
				marker: {
					size: 12,
				},
			},
			spacing: 10,
		},
	};

	return (
		<ChartContainer data={options.data} filename="vessel_pp_climate_alignment_chart_data" buttonPosition="top-right">
			<AgCharts options={options} className="w-full h-full" />
		</ChartContainer>
	);
};

export default VesselPpClimateAlignmentChart;
